import NumberInput from "../../../components/NumberInput";
import { useMemo, useRef, useState } from "react";
import CreatableSelect from "react-select/creatable";

import { formatIDR } from "../../../utils/helpers/formatIDR";
import { toUnitOptions } from "../../../utils/helpers/unitOptions";
import {
  calculatePurchaseDetailSubtotal,
  isBaseQuantityUnit,
} from "../../../utils/helpers/purchaseCalculations";
import { FormValidationNotice, RequiredMark } from "../../../components/FormValidation";
import PurchaseItemCard from "./PurchaseItemCard";
import { purchaseItemErrors } from "./purchaseItemInput";
import "./style.css";


const PAYMENT_METHOD_OPTIONS = [
  { value: "cash", label: "Tunai", icon: "cash" },
  { value: "transfer", label: "Transfer", icon: "transfer" },
  { value: "qris", label: "QRIS", icon: "qris" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px",
    borderColor: state.isFocused ? "var(--store-primary, #0f766e)" : "var(--tblr-border-color, #dce1e7)",
    borderRadius: "10px",
    backgroundColor: "var(--tblr-bg-forms, #fff)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(var(--store-primary-rgb, 15, 118, 110), .12)" : "none",
    ":hover": {
      borderColor: state.isFocused ? "var(--store-primary, #0f766e)" : "var(--tblr-border-color, #dce1e7)",
    },
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#32CD32" // item yang dipilih
        : state.isFocused
          ? "#90EE90" // saat hover
          : "#FFF", // default
      color: state.isSelected
        ? "#FFF" // item yang dipilih
        : state.isFocused
          ? "#000" // saat hover
          : "#000", // default
      cursor: "pointer",
    }),
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};


const recalculatePurchaseTotal = (details = []) =>
  details.reduce((total, detail) => total + Number(detail?.sub_total || 0), 0);

const isPurchaseAlreadyPaid = (purchase = {}) => {
  const total = Number(purchase?.total || 0);
  const pay = Number(purchase?.pay || 0);
  const due = Number(purchase?.due || 0);

  return total > 0 && (pay >= total || due === 0);
};

const syncPaymentWithTotal = (purchase = {}, total = 0, paymentReadOnly = false) => {
  const pay = paymentReadOnly
    ? Number(purchase?.pay || 0)
    : isPurchaseAlreadyPaid(purchase)
      ? total
      : Math.min(total, Math.max(0, Number(purchase?.pay || 0)));

  return {
    total,
    pay,
    due: Math.max(0, total - pay),
    payment_status: total > 0 && pay >= total ? "paid" : "unpaid",
  };
};

const updateDetailsAndPayment = (previousState = {}, details = [], paymentReadOnly = false) => {
  const total = recalculatePurchaseTotal(details);

  return {
    ...previousState,
    details,
    ...syncPaymentWithTotal(previousState, total, paymentReadOnly),
  };
};

const paymentIcon = (icon) => {
  if (icon === "qris") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 14h2v6h-4v-2h2zM14 18h2v2h-2z" />
      </svg>
    );
  }

  if (icon === "transfer") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M5 8h14M15 4l4 4-4 4M19 16H5M9 12l-4 4 4 4" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <circle cx="12" cy="12" r="3" />
      <path d="M7 12h.01M17 12h.01" />
    </svg>
  );
};

const FormPurchase = ({
  isEdit,
  paymentReadOnly = false,
  closeModal,
  formState,
  setFormState,
  handleAdd,
  handleUpdate,
  materials,
  initialState,
  suppliers,
  units,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const submitLockRef = useRef(false);
  const successCloseRef = useRef(null);
  const optionUnit = useMemo(() => toUnitOptions(units), [units]);
  const optionMaterial = useMemo(
    () =>
      (materials || []).map((item) => ({
        value: item?.id,
        label: item?.name,
        unit: item?.unit,
        weight: item?.weight,
        stock: Number(item?.stock || 0),
        category: item?.category_label || item?.category,
        type: item?.type_label || item?.type,
        unit_cost: Number(item?.price_trend?.current_price || item?.unit_cost || 0),
      })),
    [materials],
  );
  const optionSupplier = useMemo(
    () => (suppliers || []).map((item) => ({ value: item?.id, label: item?.name })),
    [suppliers],
  );

  const updateDetail = (detailId, changes) => {
    setFormState((previous) => {
      const details = previous.details.map((detail) => {
        if (detail.id !== detailId) return detail;

        const updated = typeof changes === "function" ? changes(detail) : { ...detail, ...changes };

        return {
          ...updated,
          sub_total: calculatePurchaseDetailSubtotal(updated, optionUnit),
        };
      });

      return updateDetailsAndPayment(previous, details, paymentReadOnly);
    });
  };

  const addDetail = () => {
    setFormState((previous) => ({
      ...previous,
      details: [
        ...(previous.details || []),
        {
          ...initialState.details[0],
          id: `new-${Date.now()}-${previous.details.length}`,
          qty: 1,
          unit_cost: "",
        },
      ],
    }));
  };

  const removeDetail = (detailId) => {
    setFormState((previous) =>
      updateDetailsAndPayment(
        previous,
        previous.details.filter((detail) => detail.id !== detailId),
        paymentReadOnly,
      ),
    );
  };

  const detailErrors = (detail) => purchaseItemErrors(detail, optionUnit,
    optionMaterial.find((material) => String(material.value) === String(detail.material_id))?.unit);
  const detailIsValid = (detail) => !Object.values(detailErrors(detail)).some(Boolean);

  const validDetails = (formState?.details || []).filter(
    (detail) => detail?.material_id || detail?.material_name?.trim(),
  );
  const isFormValid = validDetails.length > 0 && validDetails.every(detailIsValid) && Boolean(formState?.purchase_date);
  const due = Math.max(0, Number(formState?.total || 0) - Number(formState?.pay || 0));
  const purchaseValidationErrors = validationAttempted
    ? {
        ...(!formState?.purchase_date ? { purchase_date: "Tanggal pembelian wajib diisi." } : {}),
        ...(validDetails.length === 0 ? { details: "Minimal satu item pembelian wajib diisi." } : {}),
        ...(formState?.details || []).reduce((errors, detail, index) => {
          Object.entries(detailErrors(detail)).forEach(([field, message]) => {
            if (message) errors[`details.${index}.${field}`] = message;
          });
          return errors;
        }, {}),
      }
    : {};

  const handleSave = async () => {
    setValidationAttempted(true);
    if (!isFormValid || isSaving || submitLockRef.current) return;

    submitLockRef.current = true;

    const details = validDetails.map((detail) => {
      const { purchase_input: _purchaseInput, ...storedDetail } = detail;
      return {
        ...storedDetail,
        item_type: "material",
        is_consumable: false,
        weight: isBaseQuantityUnit(detail?.unit, optionUnit) ? Number(detail?.weight || 0) : 0,
        qty: Number(detail?.qty || 0),
        unit_cost: Number(detail?.unit_cost || 0),
        sub_total: calculatePurchaseDetailSubtotal(detail, optionUnit),
      };
    });
    const total = recalculatePurchaseTotal(details);
    const payload = {
      ...formState,
      ...syncPaymentWithTotal(formState, total, paymentReadOnly),
      details,
    };

    setIsSaving(true);
    try {
      const success = isEdit ? await handleUpdate(payload) : await handleAdd(payload);

      if (success) {
        successCloseRef.current?.click();
      }
    } finally {
      submitLockRef.current = false;
      setIsSaving(false);
    }
  };

  return (
    <div
      className="modal modal-blur fade"
      id="modal-form"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="purchase-modal-title"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.3)" }}>
      <button
        ref={successCloseRef}
        type="button"
        className="d-none"
        data-bs-dismiss="modal"
        tabIndex="-1"
        aria-hidden="true"
        onClick={closeModal}
      />
      <div className="modal-dialog modal-dialog-centered modal-xl purchase-modal-dialog" role="document">
        <div className="modal-content purchase-modal-content">
          <div className="modal-header purchase-modal-header">
            <div className="d-flex align-items-center gap-3">
              <span className="purchase-modal-icon">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M5 4h14l1 4H4zM5 8v12h14V8M8 12h8M8 16h5" />
                </svg>
              </span>
              <div>
                <div className="text-secondary small fw-semibold text-uppercase">Inventory & Keuangan</div>
                <h5 className="modal-title mb-0" id="purchase-modal-title">
                  {isEdit ? "Ubah Pembelian" : "Pembelian Baru"}
                </h5>
              </div>
            </div>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              data-purchase-close
              aria-label="Tutup modal pembelian"
              disabled={isSaving}
              onClick={closeModal}
            />
          </div>

          <div className="modal-body purchase-modal-body">
            <FormValidationNotice errors={purchaseValidationErrors} title="Lengkapi data pembelian yang ditandai." />
            {isEdit ? (
              <div className="alert purchase-edit-alert" role="note">
                <strong>Mengubah transaksi lama akan menghitung ulang stok dan HPP.</strong>
                <span> Perubahan ditolak otomatis apabila materialnya sudah dipakai dalam produksi.</span>
              </div>
            ) : null}

            <section className="purchase-section" aria-labelledby="purchase-items-title">
              <div className="purchase-section-heading">
                <div>
                  <div className="purchase-step">01</div>
                  <div>
                    <h3 id="purchase-items-title">Item pembelian</h3>
                    <p>Pembelian dikhususkan untuk material yang menambah stok inventory.</p>
                  </div>
                </div>
                <button type="button" className="btn btn-primary" onClick={addDetail}>
                  <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Tambah item
                </button>
              </div>

              <div className="purchase-item-list">
                {(formState?.details || []).length === 0 ? (
                  <div className="purchase-empty-state">
                    <strong>Belum ada item pembelian</strong>
                    <span>Tambahkan material yang dibeli dari supplier.</span>
                    <button type="button" className="btn btn-primary" onClick={addDetail}>
                      Tambah item pertama
                    </button>
                  </div>
                ) : null}

                {(formState?.details || []).map((detail, index) => {
                  const selectedIds = (formState.details || []).filter((row) => row.id !== detail.id).map((row) => String(row.material_id));
                  return (
                    <PurchaseItemCard
                      key={detail.id || `purchase-detail-${index}`}
                      detail={detail} index={index} units={optionUnit} selectStyles={selectStyles}
                      materials={optionMaterial.filter((material) => !selectedIds.includes(String(material.value)))}
                      errors={validationAttempted ? detailErrors(detail) : {}}
                      onChange={(changes) => updateDetail(detail.id, changes)}
                      onRemove={() => removeDetail(detail.id)}
                    />
                  );
                })}
              </div>
            </section>

            <div className="row g-4 purchase-bottom-grid">
              <div className="col-lg-7">
                <section className="purchase-section h-100" aria-labelledby="purchase-document-title">
                  <div className="purchase-section-heading compact">
                    <div>
                      <div className="purchase-step">02</div>
                      <div>
                        <h3 id="purchase-document-title">Informasi transaksi</h3>
                        <p>Supplier boleh dipilih dari master atau ditulis sebagai vendor satu kali.</p>
                      </div>
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-7">
                      <label className="form-label">Supplier / vendor</label>
                      <CreatableSelect
                        options={optionSupplier}
                        className="form-select p-0"
                        classNamePrefix="akas-select"
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        isClearable
                        formatCreateLabel={(value) => `Gunakan vendor “${value}”`}
                        value={
                          formState?.supplier_id
                            ? optionSupplier.find(
                                (supplier) => String(supplier.value) === String(formState.supplier_id),
                              )
                            : formState?.supplier_name
                              ? { value: formState.supplier_name, label: formState.supplier_name }
                              : null
                        }
                        placeholder="Pilih atau ketik nama vendor"
                        onChange={(selected) =>
                          setFormState((previous) => ({
                            ...previous,
                            supplier_id: selected?.__isNew__ ? "" : selected?.value || "",
                            supplier_name: selected?.label || "",
                          }))
                        }
                      />
                    </div>
                    <div className="col-md-5">
                      <label className="form-label">
                        Tanggal pembelian
                        <RequiredMark />
                      </label>
                      <input
                        type="date"
                        name="purchase_date"
                        className={`form-control purchase-control${purchaseValidationErrors.purchase_date ? " is-invalid" : ""}`}
                        value={formState?.purchase_date || ""}
                        onChange={(event) =>
                          setFormState((previous) => ({ ...previous, purchase_date: event.target.value }))
                        }
                      />
                      {purchaseValidationErrors.purchase_date ? (
                        <div className="invalid-feedback d-block">{purchaseValidationErrors.purchase_date}</div>
                      ) : null}
                    </div>
                    <div className="col-12">
                      <label className="form-label">
                        Catatan <span className="text-secondary fw-normal">(opsional)</span>
                      </label>
                      <textarea
                        className="form-control purchase-control"
                        rows="3"
                        value={formState?.note || ""}
                        placeholder="Nomor invoice supplier, termin, atau catatan internal..."
                        onChange={(event) => setFormState((previous) => ({ ...previous, note: event.target.value }))}
                      />
                    </div>
                  </div>
                </section>
              </div>

              <div className="col-lg-5">
                <section className="purchase-section purchase-payment-section" aria-labelledby="purchase-payment-title">
                  <div className="purchase-section-heading compact">
                    <div>
                      <div className="purchase-step">03</div>
                      <div>
                        <h3 id="purchase-payment-title">Pembayaran</h3>
                        <p>Boleh lunas atau dicatat sebagai hutang supplier.</p>
                      </div>
                    </div>
                  </div>

                  <div className="purchase-payment-total">
                    <span>Total tagihan</span>
                    <strong>{formatIDR(formState?.total || 0)}</strong>
                  </div>

                  <div className="purchase-payment-methods" role="radiogroup" aria-label="Metode pembayaran">
                    {PAYMENT_METHOD_OPTIONS.map((method) => (
                      <label
                        key={method.value}
                        className={`purchase-payment-method ${formState?.payment_method === method.value ? "active" : ""}`}>
                        <input
                          type="radio"
                          name="purchase_payment_method"
                          disabled={paymentReadOnly}
                          value={method.value}
                          checked={formState?.payment_method === method.value}
                          onChange={(event) =>
                            setFormState((previous) => ({ ...previous, payment_method: event.target.value }))
                          }
                        />
                        {paymentIcon(method.icon)}
                        <span>{method.label}</span>
                      </label>
                    ))}
                  </div>

                  {paymentReadOnly ? (
                    <p className="text-secondary small mt-2 mb-0">
                      Pembayaran hanya dapat diubah dengan izin Konfirmasi / Proses Pembelian.
                    </p>
                  ) : null}
                  <label className="form-label mt-3">Jumlah dibayar</label>
                  <div className="input-group purchase-payment-input">
                    <span className="input-group-text">Rp</span>
                    <NumberInput
                      type="number"
                      className="form-control"
                      min="0"
                      max={Number(formState?.total || 0)}
                      value={formState?.pay ?? ""}
                      disabled={paymentReadOnly}
                      placeholder="0"
                      onWheel={(event) => event.currentTarget.blur()}
                      onChange={(event) => {
                        const total = Number(formState?.total || 0);
                        const rawValue = event.target.value;
                        const pay = rawValue === "" ? "" : Math.min(total, Math.max(0, Number(rawValue)));
                        const numericPay = Number(pay || 0);

                        setFormState((previous) => ({
                          ...previous,
                          pay,
                          due: Math.max(0, total - numericPay),
                          payment_status: total > 0 && numericPay >= total ? "paid" : "unpaid",
                        }));
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={paymentReadOnly || Number(formState?.total || 0) <= 0}
                      onClick={() => {
                        const total = Number(formState?.total || 0);
                        setFormState((previous) => ({
                          ...previous,
                          pay: total,
                          due: 0,
                          payment_status: total > 0 ? "paid" : "unpaid",
                        }));
                      }}>
                      Bayar lunas
                    </button>
                  </div>

                  <div className="purchase-payment-status">
                    <div>
                      <span>Sisa hutang</span>
                      <strong>{formatIDR(due)}</strong>
                    </div>
                    <span
                      className={`badge ${due <= 0 && Number(formState?.total || 0) > 0 ? "bg-primary-lt" : "bg-warning-lt"}`}>
                      {due <= 0 && Number(formState?.total || 0) > 0 ? "Lunas" : "Belum lunas"}
                    </span>
                  </div>
                </section>
              </div>
            </div>
          </div>

          <div className="modal-footer purchase-modal-footer">
            <div className="purchase-footer-summary">
              <span>{validDetails.length} item</span>
              <strong>{formatIDR(formState?.total || 0)}</strong>
            </div>
            <div className="d-flex gap-2 ms-auto">
              <button
                type="button"
                className="btn btn-ghost-secondary"
                data-bs-dismiss="modal"
                disabled={isSaving}
                onClick={closeModal}>
                Batal
              </button>
              <button type="button" className="btn btn-primary" disabled={isSaving} onClick={handleSave}>
                {isSaving ? <span className="spinner-border spinner-border-sm" aria-hidden="true" /> : null}
                {isSaving ? "Menyimpan..." : isEdit ? "Simpan perubahan" : "Simpan pembelian"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormPurchase;
